import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import LoadingModal from "../../components/modals/LoadingModal";
import NotificationSnackbar from "../../components/NotificationSnackbar";
import NewAccoutModal from "../../components/modals/NewAccoutModal";
import GroupAssignmentModal from "../../components/modals/GroupAssignmentModal";
import { get, post } from "@aws-amplify/api";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { extractAccessTokenJwt } from "../../helpers/auth";
import { useAuthInfo } from "../../helpers/useAuthInfo";
import { CGI_RED, congnitoGroups } from "../../helpers/enums";
import {
  Box,
  Button,
  Container,
  IconButton,
  Typography,
  Switch,
  Tooltip,
} from "@mui/material";
import DataTable from "../../components/DataTable";

export default function ManageAccounts() {
  const { t } = useTranslation();

  const { userGroups } = useAuthInfo();

  const buttonSize = "30px";

  const emptyUsersList = [];
  const emmptyUser = {
    ID: "",
    user: "",
    groups: [],
  };
  const [userList, setUserList] = useState(emptyUsersList);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedUser, setSelectedUser] = useState(emmptyUser);
  const [openLoading, setOpenLoading] = useState(false);
  const [openAddUser, setOpenAddUser] = useState(false);
  const [openGroupAssignment, setOpenGroupAssignment] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const adminApiName = "AdminQueries";
  const hiddenHeaders = ["ID"];
  const exclude = [];
  const smallerFields = ["active", "userStatus"];
  const biggerFields = ["user"];

  const headers =
    userList.length > 0
      ? [
          ...Object.keys(userList[0])
            .filter((key) => !exclude.includes(key))
            .map((key) => {
              return {
                field: key,
                headerName: t(key),
                flex: smallerFields.includes(key)
                  ? 1
                  : biggerFields.includes(key)
                  ? 3
                  : 2,
                hide: hiddenHeaders.includes(key),
                renderCell: (params) => {
                  const userId = params.row.ID;
                  const { value } = params;
                  if (typeof value === "boolean") {
                    return (
                      <Switch
                        size="small"
                        checked={value}
                        onChange={(event) => {
                          enableUser(userId, !value);
                        }}
                      />
                    );
                  }
                  return <span>{value}</span>;
                },
              };
            }),
          {
            field: "manage",
            headerName: t("manageAccount"),
            flex: 1,
            sortable: false,
            renderCell: ({ row }) => {
              const userId = row.id;
              const user = row;
              return (
                <>
                  <Tooltip title={t("editGroups")}>
                    <IconButton
                      onClick={(e) => {
                        setSelectedUser(userList[userId]);
                        setOpenGroupAssignment(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>{" "}
                  <Tooltip title={t("deleteAccount")}>
                    <IconButton
                      onClick={(e) => {
                        setNotificationMessage("Not implemented yet");
                        setOpenNotification(true);
                      }}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                </>
              );
            },
          },
        ]
      : [];

  const rows = userList.map((row, index) => ({
    id: row.id ?? index,
    ...row,
    groups: Array.isArray(row.groups) ? row.groups.join(", ") : "",
  }));

  async function getAccessToken() {
    try {
      const session = await fetchAuthSession();
      const token = extractAccessTokenJwt(session);
      if (!token) {
        console.warn("getAccessToken: token is empty");
        throw new Error("No access token");
      }
      return token;
    } catch (err) {
      console.error("getAccessToken: failed to get token:", err);
      throw err;
    }
  }

  async function listAllGroups() {
    const path = "/listGroups";

    const token = await getAccessToken();

    const options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
    };

    const ret = await get({ apiName: adminApiName, path, options });
    const response = await (ret?.response ?? Promise.resolve(ret));

    try {
      const text = await new Response(response.body).text();

      if (!text) return [];

      const parsed = JSON.parse(text);

      const groups = parsed?.Groups ?? parsed?.groups ?? [];
      setAllGroups(Array.isArray(groups) ? groups : []);
      return groups;
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async function enableUser(username, enable) {
    setOpenLoading(true);
    try {
      const path = enable ? "/enableUser" : "/disableUser";
      const token = await getAccessToken();

      const options = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ?? "",
        },
        body: { username },
      };

      await post({ apiName: adminApiName, path, options });
      await fetchAllUsers();
      setNotificationMessage(enable ? t("enabledUser") : t("disabledUser"));
    } catch (err) {
      console.error(err);
      setNotificationMessage(t("errorMessage"));
    } finally {
      setOpenLoading(false);
      setOpenNotification(true);
    }
  }

  async function listUsersInGroup(groupname) {
    const path = "/listUsersInGroup";

    if (!groupname) return [];

    const token = await getAccessToken();

    const options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      queryParams: {
        groupname: groupname,
      },
    };

    const ret = await get({ apiName: adminApiName, path, options });
    const response = await (ret?.response ?? Promise.resolve(ret));

    try {
      const text = await new Response(response.body).text();

      if (!text) return [];

      const parsed = JSON.parse(text);

      return Array.isArray(parsed?.Users) ? parsed.Users : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  async function fetchAllUsers() {
    setOpenLoading(true);
    try {
      const groups = await listAllGroups();
      console.log("Fetching all Groups:", groups);

      if (!Array.isArray(groups) || groups.length === 0) {
        setUserList([]);
        return;
      }

      const groupPromises = groups.map(async (g) => {
        const groupName = g?.GroupName;
        try {
          const users = await listUsersInGroup(g?.GroupName);
          return { group: groupName, users: users || [] };
        } catch (e) {
          console.error(e);
          return { group: groupName, users: [] };
        }
      });

      const results = await Promise.all(groupPromises);

      const userMap = new Map();
      for (const { group, users } of results) {
        for (const user of users) {
          const cognitoUsername = user?.Username ?? "";
          const emailAttr = (user?.Attributes || []).find(
            (attr) => attr.Name?.toLowerCase() === "email"
          );
          const email = emailAttr?.Value ?? "";
          const uniqueKey = cognitoUsername || email || JSON.stringify(user);

          const displayName = email || cognitoUsername || "(no-username)";
          const userStatus =
            user?.UserStatus === "CONFIRMED"
              ? t("confirmed")
              : t("unconfirmed");

          if (!userMap.has(uniqueKey)) {
            userMap.set(uniqueKey, {
              ID: cognitoUsername,
              user: displayName,
              userStatus,
              groups: [group],
              active: !!user?.Enabled,
            });
          } else {
            const existingUser = userMap.get(uniqueKey);
            if (!existingUser.groups.includes(group)) {
              existingUser.groups.push(group);
            }
          }
        }
      }

      const usersArray = Array.from(userMap.values());
      setUserList(usersArray);
    } catch (err) {
      console.error(err);
      setNotificationMessage?.(t?.("errorMessage") ?? "Error");
      setOpenNotification?.(true);
    } finally {
      setOpenLoading(false);
    }
  }

  async function addToGroup(username, groupname) {
    setOpenLoading(true);
    try {
      const path = "/addUserToGroup";

      let token = null;
      try {
        token = await getAccessToken();
      } catch (err) {
        console.error(err);
        throw err;
      }

      const options = {
        headers: {
          "Content-Type": "application/json",
          Authorization: token ?? "",
        },
        body: {
          username,
          groupname,
        },
      };

      await post({
        apiName: adminApiName,
        path,
        options,
      });

      try {
        await fetchAllUsers();
      } catch (e) {
        console.error(e);
      }

      setNotificationMessage(t("updated") ?? "Updated");
    } catch (err) {
      console.error(err);
      setNotificationMessage(t("errorMessage") ?? "Error");
    } finally {
      setOpenNotification(true);
      setOpenLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!userGroups) return;
      if (!userGroups.includes(congnitoGroups.ADMIN)) return;
      try {
        await fetchAllUsers();
      } catch (err) {
        console.error(err);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [userGroups]);

  return (
    <>
      <Box sx={{ mt: 5 }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              borderBottom: 1,
              borderColor: CGI_RED,
            }}
          >
            <Typography
              sx={{
                color: CGI_RED,
                fontWeight: 500,
                fontSize: 23,
              }}
            >
              {t("manageAccounts")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddUser(true)}
            >
              {t("addUser")}
            </Button>
          </Box>
          <Box>
            <DataTable
              headers={headers}
              data={rows}
              hiddenColumns={hiddenHeaders}
            />
          </Box>
        </Container>
        <LoadingModal open={openLoading} />
        <NewAccoutModal
          open={openAddUser}
          setOpen={setOpenAddUser}
          addToGroup={addToGroup}
          getUsersWithGroup={fetchAllUsers}
          setOpenLoading={setOpenLoading}
          setNotificationMessage={setNotificationMessage}
          setOpenNotification={setOpenNotification}
          adminApiName={adminApiName}
        />
        <GroupAssignmentModal
          open={openGroupAssignment}
          setOpen={setOpenGroupAssignment}
          buttonSize={buttonSize}
          userId={selectedUser.ID}
          username={selectedUser.user}
          fetchData={fetchAllUsers}
          usersGroups={selectedUser.groups}
          allGroups={allGroups}
          addToGroup={addToGroup}
          setNotificationMessage={setNotificationMessage}
          setOpenNotification={setOpenNotification}
        />
      </Box>
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
    </>
  );
}
